import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import ImpersonationBanner from "./components/ImpersonationBanner";

import Home from "./pages/Home";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import SellerShop from "./pages/SellerShop";
import Categories from "./pages/Categories";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import Orders from "./pages/Orders";
import OrderDetail from "./pages/OrderDetail";
import Wishlist from "./pages/Wishlist";
import Account from "./pages/Account";
import Affiliate from "./pages/Affiliate";
import AffiliateLeaderboard from "./pages/AffiliateLeaderboard";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";

import SellerDashboard from "./pages/seller/SellerDashboard";
import SellerProducts from "./pages/seller/SellerProducts";
import SellerOrders from "./pages/seller/SellerOrders";
import SellerEarnings from "./pages/seller/SellerEarnings";
import SellerApply from "./pages/seller/SellerApply";
import SellerDiscounts from "./pages/seller/SellerDiscounts";

import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminWhatsApp from "./pages/admin/AdminWhatsApp";
import AdminPosSync from "./pages/admin/AdminPosSync";
import AdminTrackmatrix from "./pages/admin/AdminTrackmatrix";
import AdminAffiliates from "./pages/admin/AdminAffiliates";
import AdminDeletedOrders from "./pages/admin/AdminDeletedOrders";
import AdminOrders from "./pages/admin/AdminOrders";

function AppRouter() {
  const location = useLocation();
  // CRITICAL: detect session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/shop/:id" element={<SellerShop />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/login" element={<Login />} />
        <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
        <Route path="/orders/:id" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
        <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
        <Route path="/affiliate" element={<ProtectedRoute roles={["seller", "admin"]}><Affiliate /></ProtectedRoute>} />
        <Route path="/leaderboard" element={<ProtectedRoute roles={["seller", "admin"]}><AffiliateLeaderboard /></ProtectedRoute>} />
        <Route path="/seller/apply" element={<ProtectedRoute><SellerApply /></ProtectedRoute>} />

        <Route
          path="/seller"
          element={<ProtectedRoute roles={["seller", "admin"]}><SellerDashboard /></ProtectedRoute>}
        >
          <Route path="products" element={<SellerProducts />} />
          <Route path="orders" element={<SellerOrders />} />
          <Route path="earnings" element={<SellerEarnings />} />
          <Route path="discounts" element={<SellerDiscounts />} />
        </Route>

        <Route
          path="/admin"
          element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>}
        >
          <Route path="users" element={<AdminUsers />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="whatsapp" element={<AdminWhatsApp />} />
          <Route path="pos" element={<AdminPosSync />} />
          <Route path="trackmatrix" element={<AdminTrackmatrix />} />
          <Route path="affiliates" element={<AdminAffiliates />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="deleted-orders" element={<AdminDeletedOrders />} />
        </Route>
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App overflow-x-hidden max-w-full">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <ImpersonationBanner />
            <AppRouter />
            <Toaster position="top-center" richColors />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
